import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InjectedFormProps, reduxForm } from 'redux-form';
import Markdown from 'shared/components/Markdown';
import { SPACINGS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import Button, { ButtonType } from 'inquiry/components/Button';
import { EMAIL_ADDRESS_REGEX } from 'lib/constants/regexes';
import FormControlField from 'lib/interfaces/FormControlField';
import ThemeSerialized, { TermsTextPosition } from 'lib/interfaces/ThemeSerialized';
import { ButtonComponentStyles, TextComponentStyles } from 'lib/interfaces/ui-components';
import { isRequired, matchesRegexp, withMessage } from 'lib/utils/formValidation';
import FormGroup from 'verify/components/FormGroup';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer';
import ThemedFormInput from 'verify/components/ThemedFormInput';
import ThemedParagraph from 'verify/components/ThemedParagraph';

const DisclaimerContainer = styled.div<{ position: TermsTextPosition; theme: ThemeSerialized }>`
  display: block;
  margin-top: ${SPACINGS.base}px;
  ${({ position, theme }) =>
    position === TermsTextPosition.AboveButton &&
    css`
      padding-bottom: ${SPACINGS.base * 2}px;
      border-bottom: 1px solid ${theme.colorDivider};
      margin-bottom: ${SPACINGS.base * 2}px;
    `}
`;

const Disclaimer = styled(ThemedParagraph)<{ theme: ThemeSerialized }>`
  color: ${({ theme }) => theme.colorFontSmall};
  font-size: ${({ theme }) => theme.fontSizeSmall};
  line-height: ${({ theme }) => theme.lineHeightSmall};

  &,
  a {
    font-size: ${({ theme }) => theme.fontSizeSmall};
    line-height: ${({ theme }) => theme.lineHeightSmall};
  }
`;

export interface FormEmailAddressProps {
  className?: string;
  labelContinue: string;
  buttonStyle?: ButtonComponentStyles;
  disclaimerStyle?: TextComponentStyles;
  disclaimerText?: string;
  disclaimerPosition?: TermsTextPosition;
  disabled?: boolean;
  // TODO: input SST styles
}

function FormEmailAddressComponent({
  handleSubmit,
  labelContinue,
  pristine,
  submitting,
  buttonStyle,
  disclaimerStyle,
  disclaimerText,
  disclaimerPosition = TermsTextPosition.BelowButton,
  disabled,
}: FormEmailAddressProps & InjectedFormProps<{}, FormEmailAddressProps>) {
  const [t] = useTranslation();

  const validate = useMemo(() => {
    return {
      emailAddress: [
        withMessage(t('database.form.validate-email-valid'), matchesRegexp(EMAIL_ADDRESS_REGEX)),
        withMessage(t('database.form.validate-email-required'), isRequired),
      ],
    };
  }, [t]);

  const disclaimer = (
    <>
      {disclaimerStyle != null ? (
        <ThemedParagraph styles={disclaimerStyle}>
          <Markdown>{disclaimerText ?? t('to-email.disclaimer')}</Markdown>
        </ThemedParagraph>
      ) : (
        <Disclaimer>
          <Markdown>{disclaimerText ?? t('to-email.disclaimer')}</Markdown>
        </Disclaimer>
      )}
    </>
  );

  return (
    <form onSubmit={handleSubmit} data-test='form-email-address'>
      <FormGroup>
        <label htmlFor='email-address'>{t('email-address.form.label')}</label>
        <FormControlField
          autoComplete='email'
          id='email-address'
          name='email-address'
          component={ThemedFormInput}
          disabled={disabled || submitting}
          inputMode='email'
          placeholder='you@example.com'
          validate={validate.emailAddress}
        />
      </FormGroup>

      {disclaimerPosition === TermsTextPosition.AboveButton && (
        <DisclaimerContainer position={TermsTextPosition.AboveButton}>
          {disclaimer}
        </DisclaimerContainer>
      )}

      <PositionableButtonContainer>
        <Button
          type='submit'
          buttonType={ButtonType.Primary}
          styles={buttonStyle}
          disabled={submitting}
          isPending={submitting}
          tabIndex={pristine || submitting ? -1 : 0}
        >
          {labelContinue}
        </Button>

        {disclaimerPosition === TermsTextPosition.BelowButton && (
          <DisclaimerContainer position={TermsTextPosition.BelowButton}>
            {disclaimer}
          </DisclaimerContainer>
        )}
      </PositionableButtonContainer>
    </form>
  );
}

const FormEmailAddress = reduxForm<{}, FormEmailAddressProps>({
  form: 'email-address',
})(FormEmailAddressComponent);

export default FormEmailAddress;
